import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export interface CategoryStat {
  id: number;
  name: string;
  trainCount: number;
  testCount: number;
  totalCount: number;
}

interface CategoryRow {
  id: number;
  name: string;
  sort_order: number | null;
}

interface ArticleSplitRow {
  id: number;
  category_id: number | null;
  data_split: string | null;
}

export function useModelArticleData() {
  const [categories, setCategories] = useState<CategoryStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoryRes, articleRes] = await Promise.all([
        supabase.from("article_categories").select("id, name, sort_order").order("sort_order"),
        supabase.from("dataset_articles").select("id, category_id, data_split"),
      ]);

      if (categoryRes.error) throw categoryRes.error;
      if (articleRes.error) throw articleRes.error;

      const articles = (articleRes.data ?? []) as ArticleSplitRow[];
      const stats: CategoryStat[] = ((categoryRes.data ?? []) as CategoryRow[]).map((category) => {
        const rows = articles.filter((article) => article.category_id === category.id);
        return {
          id: category.id,
          name: category.name,
          trainCount: rows.filter((row) => row.data_split === "train").length,
          testCount: rows.filter((row) => row.data_split === "test").length,
          totalCount: rows.length,
        };
      });

      setCategories(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : "训练数据加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSplit = useCallback(
    async (split: "train" | "test") => {
      const { error: updateError } = await supabase
        .from("dataset_articles")
        .update({ data_split: "none" })
        .eq("data_split", split);
      if (updateError) throw updateError;
      await load();
    },
    [load]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { categories, loading, error, reload: load, clearSplit };
}